import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaMapMarkerAlt,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTimesCircle,
  FaUsers,
} from "react-icons/fa";
import axios from "axios";

import PartnerSidebar from "../PartnerSidebar";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const EVENT_TYPES = [
  "Live Performance",
  "Workshop",
  "Theatre Production",
  "Exhibition",
  "Festival",
  "Corporate Event",
  "Other",
];

// Placeholder collab list until a real POST /partner/collabs endpoint exists.
// Shape mirrors what the create-form produces, so swapping to a real API
// later just means replacing setCollabs(prev => [...]) with a refetch.
const seedCollabs = [
  {
    id: "COLLAB-2026-004",
    name: "Monsoon Beats Showcase",
    type: "Live Performance",
    location: "Nagpur",
    eventDate: "2026-09-20",
    artists: [
      { id: "a1", name: "Aarav Mehta", avatar: null },
      { id: "a2", name: "Ishita Rao", avatar: null },
    ],
    status: "Ongoing",
  },
  {
    id: "COLLAB-2026-003",
    name: "Classical Fusion Night",
    type: "Festival",
    location: "Pune",
    eventDate: "2026-08-05",
    artists: [{ id: "a3", name: "Devansh Kulkarni", avatar: null }],
    status: "Completed",
  },
];

const styles = `
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap");

.collab-layout {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: var(--collab-background);
  font-family: "Inter", sans-serif;
  --collab-maroon: #ad2f3b;
  --collab-maroon-dark: #7f1f2b;
  --collab-maroon-light: #f8e9ec;
  --collab-black: #171717;
  --collab-dark-grey: #3e3e3e;
  --collab-grey: #666666;
  --collab-background: #f4f4f4;
  --collab-white: #ffffff;
  --collab-border: #dcdcdc;
  --collab-green: #16734b;
  --collab-amber: #a86d00;
  --collab-red: #b4233b;
  --collab-shadow: 0 5px 18px rgba(0, 0, 0, 0.065);
}

.collab-layout > .sidebar {
  position: fixed !important;
  top: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  z-index: 1000;
  height: 100vh !important;
  margin: 0 !important;
}

.collab-main {
  width: calc(100% - 80px);
  height: 100vh;
  margin-left: 80px;
  padding: 20px 22px 30px;
  overflow-x: hidden;
  overflow-y: auto;
  background: var(--collab-background);
}

.collab-container {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
}

.collab-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.collab-page-header h1 {
  margin: 0;
  color: var(--collab-maroon);
  font-size: 34px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.collab-page-header p {
  max-width: 760px;
  margin: 7px 0 0;
  color: var(--collab-grey);
  font-size: 14px;
  line-height: 1.6;
}

.collab-create-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 43px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--collab-maroon);
  color: var(--collab-white);
  font-family: inherit;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.collab-create-button:hover {
  background: var(--collab-maroon-dark);
}

.collab-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.collab-summary-card {
  position: relative;
  min-height: 92px;
  overflow: hidden;
  padding: 18px 20px;
  border: 1px solid var(--collab-border);
  border-radius: 11px;
  background: var(--collab-white);
  box-shadow: var(--collab-shadow);
}

.collab-summary-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  content: "";
  background: var(--collab-maroon);
}

.collab-summary-card span {
  display: block;
  color: var(--collab-grey);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.collab-summary-card strong {
  display: block;
  margin-top: 8px;
  color: var(--collab-black);
  font-size: 28px;
}

.collab-card {
  border: 1px solid var(--collab-border);
  border-radius: 12px;
  background: var(--collab-white);
  box-shadow: var(--collab-shadow);
  margin-bottom: 18px;
  overflow: hidden;
}

.collab-card:last-child { margin-bottom: 0; }

.collab-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 20px 20px 0;
  margin-bottom: 18px;
}

.collab-filter-header h2,
.collab-table-header h2 {
  margin: 0;
  color: var(--collab-black);
  font-size: 17px;
  font-weight: 800;
}

.collab-filter-header p,
.collab-table-header p {
  margin: 5px 0 0;
  color: var(--collab-grey);
  font-size: 11px;
  line-height: 1.5;
}

.collab-clear-button {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid var(--collab-maroon);
  border-radius: 7px;
  color: var(--collab-maroon);
  background: transparent;
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.collab-clear-button:hover {
  color: var(--collab-white);
  background: var(--collab-maroon);
}

.collab-filter-grid {
  display: grid;
  grid-template-columns: minmax(320px, 1.6fr) repeat(3, minmax(180px, 0.8fr));
  gap: 12px;
  align-items: end;
  padding: 0 20px 20px;
}

.collab-search-field {
  display: flex;
  height: 43px;
  align-items: center;
  gap: 10px;
  padding: 0 13px;
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  color: var(--collab-grey);
  background: var(--collab-white);
}

.collab-search-field:focus-within {
  border-color: var(--collab-maroon);
  box-shadow: 0 0 0 3px rgba(173, 47, 59, 0.1);
}

.collab-search-field input,
.collab-date-input input {
  width: 100%;
  border: none;
  outline: none;
  color: var(--collab-black);
  background: transparent;
  font-family: inherit;
  font-size: 11px;
}

.collab-filter-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
}

.collab-filter-field > span {
  color: var(--collab-dark-grey);
  font-size: 9px;
  font-weight: 700;
}

.collab-filter-field select,
.collab-date-input {
  width: 100%;
  height: 43px;
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  background: var(--collab-white);
}

.collab-filter-field select {
  padding: 0 11px;
  outline: none;
  color: var(--collab-black);
  font-family: inherit;
  font-size: 11px;
}

.collab-date-input {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 11px;
  color: var(--collab-grey);
}

.collab-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 20px;
}

.collab-table-wrapper {
  width: 100%;
  overflow-x: auto;
  border-top: 1px solid var(--collab-border);
}

.collab-table {
  width: 100%;
  min-width: 1040px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.collab-table th {
  height: 50px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--collab-border);
  color: #3f3f3f;
  background: #eeeeee;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.035em;
  line-height: 1.3;
  text-align: left;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
}

.collab-table td {
  height: 66px;
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  color: #555555;
  font-size: 11px;
  line-height: 1.45;
  vertical-align: middle;
  white-space: normal;
  overflow-wrap: anywhere;
}

.collab-table tbody tr:nth-child(even) { background: #fbfbfb; }
.collab-table tbody tr:hover { background: #faf0f2; }
.collab-table tbody tr:last-child td { border-bottom: none; }

.collab-table th:nth-child(1),
.collab-table td:nth-child(1) { width: 14%; }

.collab-table th:nth-child(2),
.collab-table td:nth-child(2) { width: 20%; }

.collab-table th:nth-child(3),
.collab-table td:nth-child(3) { width: 14%; }

.collab-table th:nth-child(4),
.collab-table td:nth-child(4) { width: 14%; }

.collab-table th:nth-child(5),
.collab-table td:nth-child(5) { width: 14%; }

.collab-table th:nth-child(6),
.collab-table td:nth-child(6) { width: 12%; text-align: center; }

.collab-table th:nth-child(7),
.collab-table td:nth-child(7) { width: 12%; text-align: center; }

.collab-id {
  color: var(--collab-maroon);
  font-size: 11px;
  font-weight: 800;
}

.collab-artist-stack {
  display: flex;
  align-items: center;
}

.collab-artist-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--collab-maroon-light);
  color: var(--collab-maroon);
  font-weight: 800;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid var(--collab-white);
  margin-left: -8px;
}

.collab-artist-avatar:first-child {
  margin-left: 0;
}

.collab-artist-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.collab-artist-more {
  background: #e8e8e8;
  color: var(--collab-dark-grey);
}

.collab-status {
  display: inline-flex;
  min-width: 104px;
  min-height: 30px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 11px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
}

.collab-status.ongoing { color: var(--collab-amber); background: #fff3da; }
.collab-status.completed { color: var(--collab-green); background: #e7f5ee; }
.collab-status.cancelled { color: var(--collab-red); background: var(--collab-maroon-light); }

.collab-empty-state {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--collab-grey);
}

.collab-empty-state > svg {
  margin-bottom: 12px;
  color: var(--collab-maroon);
  font-size: 36px;
}

.collab-empty-state strong {
  color: var(--collab-black);
  font-size: 14px;
}

.collab-empty-state p {
  margin: 7px 0 0;
  font-size: 10px;
}

.collab-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 23, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
}

.collab-modal {
  background: var(--collab-white);
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  font-family: "Inter", sans-serif;
}

.collab-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--collab-border);
  position: sticky;
  top: 0;
  background: var(--collab-white);
}

.collab-modal-header h2 {
  margin: 0;
  color: var(--collab-black);
  font-size: 18px;
  font-weight: 800;
}

.collab-modal-close {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: var(--collab-grey);
  padding: 6px;
}

.collab-modal-close:hover { color: var(--collab-maroon); }

.collab-form {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.collab-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.collab-form-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 9px;
  font-weight: 700;
  color: var(--collab-dark-grey);
}

.collab-form-field-full {
  grid-column: 1 / -1;
}

.collab-form-field input,
.collab-form-field select,
.collab-form-field textarea {
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-family: inherit;
  font-weight: 400;
  color: var(--collab-black);
  resize: vertical;
}

.collab-form-field input:focus,
.collab-form-field select:focus,
.collab-form-field textarea:focus,
.collab-search-field:focus-within {
  outline: none;
  border-color: var(--collab-maroon);
  box-shadow: 0 0 0 3px rgba(173, 47, 59, 0.1);
}

.collab-form-field .collab-date-input {
  padding: 0 12px;
  height: 41px;
}

.collab-artist-picker {
  border: 1px solid var(--collab-border);
  border-radius: 10px;
  overflow: hidden;
}

.collab-artist-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f6f6f6;
  border-bottom: 1px solid var(--collab-border);
  font-size: 11px;
  font-weight: 700;
  color: var(--collab-dark-grey);
  flex-wrap: wrap;
}

.collab-artist-search {
  min-width: 200px;
  height: 36px;
}

.collab-artist-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.collab-artist-state {
  padding: 16px;
  text-align: center;
  color: var(--collab-grey);
  font-size: 12px;
}

.collab-artist-error {
  color: var(--collab-red);
}

.collab-artist-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
}

.collab-artist-row:hover {
  background: #faf0f2;
}

.collab-artist-row.selected {
  background: var(--collab-maroon-light);
}

.collab-artist-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--collab-maroon);
}

.collab-artist-info {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 400;
}

.collab-artist-info strong {
  color: var(--collab-black);
  font-weight: 700;
}

.collab-artist-info small {
  color: var(--collab-grey);
  font-size: 10px;
  font-weight: 400;
}

.collab-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.collab-btn-secondary,
.collab-btn-primary {
  min-height: 40px;
  padding: 0 20px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 700;
  font-size: 11px;
  cursor: pointer;
  border: none;
}

.collab-btn-secondary {
  background: #eeeeee;
  color: var(--collab-dark-grey);
}

.collab-btn-secondary:hover { background: #e2e2e2; }

.collab-btn-primary {
  background: var(--collab-maroon);
  color: var(--collab-white);
}

.collab-btn-primary:hover:not(:disabled) { background: var(--collab-maroon-dark); }

.collab-btn-primary:disabled {
  background: #d9a7ac;
  cursor: not-allowed;
}

@media (max-width: 1100px) {
  .collab-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .collab-search-field {
    grid-column: 1 / -1;
  }
}

@media (max-width: 950px) {
  .collab-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .collab-main {
    width: calc(100% - 68px);
    margin-left: 68px;
    padding: 15px;
  }

  .collab-page-header h1 {
    font-size: 30px;
  }

  .collab-form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .collab-main {
    width: calc(100% - 62px);
    margin-left: 62px;
    padding: 10px;
  }

  .collab-summary-grid,
  .collab-filter-grid {
    grid-template-columns: 1fr;
  }

  .collab-search-field {
    grid-column: auto;
  }

  .collab-filter-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .collab-page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .collab-create-button {
    justify-content: center;
  }
}
`;

const PartnerCollaborators = () => {
  const [collabs, setCollabs] = useState(seedCollabs);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [artists, setArtists] = useState([]);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const [artistsError, setArtistsError] = useState("");
  const [artistSearch, setArtistSearch] = useState("");
  const [selectedArtistIds, setSelectedArtistIds] = useState([]);

  const [form, setForm] = useState({
    name: "",
    type: EVENT_TYPES[0],
    location: "",
    eventDate: "",
    description: "",
  });

  useEffect(() => {
    if (!showCreateModal || artists.length > 0) return;

    const fetchArtists = async () => {
      setArtistsLoading(true);
      setArtistsError("");
      try {
        const { data } = await apiClient.get("admin/users", {
          params: { role: "Artist" },
        });

        const list = (data?.data || []).map((u) => ({
          id: u._id,
          name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
          avatar: u.avatar?.url || null,
          city: u.address?.city || "",
          artCategory: u.artInfo?.artCategory || "",
          artType: u.artInfo?.artType || "",
        }));

        setArtists(list);
      } catch (err) {
        setArtistsError(
          err?.response?.data?.message || "Could not load artists. Try again."
        );
      } finally {
        setArtistsLoading(false);
      }
    };

    fetchArtists();
  }, [showCreateModal, artists.length]);

  const filteredArtists = useMemo(() => {
    const search = artistSearch.trim().toLowerCase();
    if (!search) return artists;
    return artists.filter((a) =>
      [a.name, a.artCategory, a.artType, a.city].join(" ").toLowerCase().includes(search)
    );
  }, [artists, artistSearch]);

  const filteredCollabs = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return collabs.filter((collab) => {
      const matchesSearch =
        !search ||
        [collab.id, collab.name, collab.location, ...collab.artists.map((a) => a.name)]
          .join(" ")
          .toLowerCase()
          .includes(search);

      const matchesStatus = statusFilter === "All" || collab.status === statusFilter;
      const matchesFromDate = !fromDate || collab.eventDate >= fromDate;
      const matchesToDate = !toDate || collab.eventDate <= toDate;

      return matchesSearch && matchesStatus && matchesFromDate && matchesToDate;
    });
  }, [collabs, searchText, statusFilter, fromDate, toDate]);

  const summary = useMemo(
    () => ({
      total: collabs.length,
      ongoing: collabs.filter((c) => c.status === "Ongoing").length,
      completed: collabs.filter((c) => c.status === "Completed").length,
      cancelled: collabs.filter((c) => c.status === "Cancelled").length,
    }),
    [collabs]
  );

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("All");
    setFromDate("");
    setToDate("");
  };

  const formatDate = (date) =>
    new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getStatusIcon = (status) => {
    if (status === "Completed") return <FaCheckCircle />;
    if (status === "Cancelled") return <FaTimesCircle />;
    return <FaClock />;
  };

  const toggleArtist = (id) => {
    setSelectedArtistIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const resetCreateForm = () => {
    setForm({ name: "", type: EVENT_TYPES[0], location: "", eventDate: "", description: "" });
    setSelectedArtistIds([]);
    setArtistSearch("");
  };

  const closeModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const handleCreateCollab = (event) => {
    event.preventDefault();
    if (!form.name.trim() || selectedArtistIds.length === 0 || !form.eventDate) return;

    const chosenArtists = artists.filter((a) => selectedArtistIds.includes(a.id));

    const newCollab = {
      id: `COLLAB-${Date.now()}`,
      name: form.name.trim(),
      type: form.type,
      location: form.location.trim(),
      eventDate: form.eventDate,
      description: form.description.trim(),
      artists: chosenArtists.map((a) => ({ id: a.id, name: a.name, avatar: a.avatar })),
      status: "Ongoing",
    };

    // TODO: replace with POST /api/v1/partner/collabs once that endpoint exists
    setCollabs((prev) => [newCollab, ...prev]);
    closeModal();
  };

  return (
    <div className="collab-layout">
      <style>{styles}</style>

      <PartnerSidebar />

      <main className="collab-main">
        <div className="collab-container">
          <header className="collab-page-header">
            <div>
              <h1>Collaborators</h1>
              <p>Team up with artists on the platform and track collab events.</p>
            </div>

            <button
              type="button"
              className="collab-create-button"
              onClick={() => setShowCreateModal(true)}
            >
              <FaPlus /> Create Collab
            </button>
          </header>

          <section className="collab-summary-grid">
            <SummaryCard label="Total Collabs" value={summary.total} />
            <SummaryCard label="Ongoing" value={summary.ongoing} />
            <SummaryCard label="Completed" value={summary.completed} />
            <SummaryCard label="Cancelled" value={summary.cancelled} />
          </section>

          <section className="collab-card">
            <div className="collab-filter-header">
              <div>
                <h2>Search & Filters</h2>
                <p>Filter collabs by status, date, name, artist, or location.</p>
              </div>

              <button type="button" className="collab-clear-button" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>

            <div className="collab-filter-grid">
              <label className="collab-search-field">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search collab, artist or location..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </label>

              <label className="collab-filter-field">
                <span>Status</span>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </label>

              <label className="collab-filter-field">
                <span>From Date</span>
                <div className="collab-date-input">
                  <FaCalendarAlt />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(event) => setFromDate(event.target.value)}
                  />
                </div>
              </label>

              <label className="collab-filter-field">
                <span>To Date</span>
                <div className="collab-date-input">
                  <FaCalendarAlt />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(event) => setToDate(event.target.value)}
                  />
                </div>
              </label>
            </div>
          </section>

          <section className="collab-card">
            <div className="collab-table-header">
              <div>
                <h2>Collab List</h2>
                <p>Showing {filteredCollabs.length} of {collabs.length} collabs</p>
              </div>
            </div>

            <div className="collab-table-wrapper">
              <table className="collab-table">
                <thead>
                  <tr>
                    <th>Collab ID</th>
                    <th>Name</th>
                    <th>Artists</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Event Date</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredCollabs.length > 0 ? (
                    filteredCollabs.map((collab) => (
                      <tr key={collab.id}>
                        <td><strong className="collab-id">{collab.id}</strong></td>
                        <td><strong>{collab.name}</strong></td>
                        <td>
                          <div className="collab-artist-stack">
                            {collab.artists.slice(0, 3).map((a) => (
                              <span key={a.id} className="collab-artist-avatar" title={a.name}>
                                {a.avatar ? (
                                  <img src={a.avatar} alt={a.name} />
                                ) : (
                                  a.name.charAt(0)
                                )}
                              </span>
                            ))}
                            {collab.artists.length > 3 && (
                              <span className="collab-artist-avatar collab-artist-more">
                                +{collab.artists.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{collab.type}</td>
                        <td>{collab.location || "—"}</td>
                        <td>{formatDate(collab.eventDate)}</td>
                        <td>
                          <span className={`collab-status ${collab.status.toLowerCase()}`}>
                            {getStatusIcon(collab.status)}
                            {collab.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className="collab-empty-state">
                          <FaUsers />
                          <strong>No collabs found</strong>
                          <p>Change the filters, or create a new collab to get started.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>

      {showCreateModal && (
        <div className="collab-modal-overlay" onClick={closeModal}>
          <div className="collab-modal" onClick={(event) => event.stopPropagation()}>
            <div className="collab-modal-header">
              <h2>Create Collab</h2>
              <button type="button" className="collab-modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <form className="collab-form" onSubmit={handleCreateCollab}>
              <div className="collab-form-grid">
                <label className="collab-form-field">
                  <span>Collab / Group Name</span>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Monsoon Beats Showcase"
                    value={form.name}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                  />
                </label>

                <label className="collab-form-field">
                  <span>Event Type</span>
                  <select
                    value={form.type}
                    onChange={(event) => setForm({ ...form, type: event.target.value })}
                  >
                    {EVENT_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </label>

                <label className="collab-form-field">
                  <span>Location</span>
                  <div className="collab-date-input">
                    <FaMapMarkerAlt />
                    <input
                      type="text"
                      placeholder="City / venue"
                      value={form.location}
                      onChange={(event) => setForm({ ...form, location: event.target.value })}
                    />
                  </div>
                </label>

                <label className="collab-form-field">
                  <span>Event Date</span>
                  <div className="collab-date-input">
                    <FaCalendarAlt />
                    <input
                      type="date"
                      required
                      value={form.eventDate}
                      onChange={(event) => setForm({ ...form, eventDate: event.target.value })}
                    />
                  </div>
                </label>

                <label className="collab-form-field collab-form-field-full">
                  <span>Description</span>
                  <textarea
                    rows={3}
                    placeholder="What's the collab about?"
                    value={form.description}
                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                  />
                </label>
              </div>

              <div className="collab-artist-picker">
                <div className="collab-artist-picker-header">
                  <span>Select Artists ({selectedArtistIds.length} selected)</span>
                  <label className="collab-search-field collab-artist-search">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Search artists..."
                      value={artistSearch}
                      onChange={(event) => setArtistSearch(event.target.value)}
                    />
                  </label>
                </div>

                <div className="collab-artist-list">
                  {artistsLoading && <p className="collab-artist-state">Loading artists…</p>}
                  {!artistsLoading && artistsError && (
                    <p className="collab-artist-state collab-artist-error">{artistsError}</p>
                  )}
                  {!artistsLoading && !artistsError && filteredArtists.length === 0 && (
                    <p className="collab-artist-state">No artists match your search.</p>
                  )}

                  {!artistsLoading &&
                    !artistsError &&
                    filteredArtists.map((artist) => {
                      const checked = selectedArtistIds.includes(artist.id);
                      return (
                        <label
                          key={artist.id}
                          className={`collab-artist-row ${checked ? "selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleArtist(artist.id)}
                          />
                          <span className="collab-artist-avatar" aria-hidden="true">
                            {artist.avatar ? (
                              <img src={artist.avatar} alt="" />
                            ) : (
                              artist.name.charAt(0) || "?"
                            )}
                          </span>
                          <span className="collab-artist-info">
                            <strong>{artist.name || "Unnamed artist"}</strong>
                            <small>
                              {[artist.artCategory, artist.artType, artist.city]
                                .filter(Boolean)
                                .join(" • ")}
                            </small>
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>

              <div className="collab-form-actions">
                <button type="button" className="collab-btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="collab-btn-primary"
                  disabled={!form.name.trim() || !form.eventDate || selectedArtistIds.length === 0}
                >
                  Create Collab
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <article className="collab-summary-card">
    <span>{label}</span>
    <strong>{value}</strong>
  </article>
);

export default PartnerCollaborators;